import { ConsumerMessage } from './message.types';
import { parseServerTimestamp } from '../../utils/parseServerDate';
import { showDateFromTodayUpdate } from '../../utils/showDateFromToday';

export type MessagesGroup = {
  date: string;
  messages: ConsumerMessage[];
};

const compareByCreatedAt = (first: ConsumerMessage, second: ConsumerMessage) => {
  const difference = parseServerTimestamp(first.createdAt) - parseServerTimestamp(second.createdAt);
  return difference !== 0 ? difference : first.id.localeCompare(second.id);
};

/**
 * Merges message pages into a single chronological list (oldest first).
 * Duplicates are collapsed by id, the copy from the last list wins, so passing
 * the freshest page last keeps its `status` and `finalMessage` values.
 */
export const mergeConsumerMessages = (
  ...lists: (ConsumerMessage[] | undefined)[]
): ConsumerMessage[] => {
  const messagesById = new Map<string, ConsumerMessage>();
  lists.forEach((list) => list?.forEach((message) => messagesById.set(message.id, message)));
  return Array.from(messagesById.values()).sort(compareByCreatedAt);
};

/** Splits a chronological list into date groups, keeping the order of the list. */
export const groupMessagesByDate = (messages: ConsumerMessage[]): MessagesGroup[] =>
  messages.reduce<MessagesGroup[]>((groups, message) => {
    const date = showDateFromTodayUpdate(message.createdAt);
    const currentGroup = groups[groups.length - 1];
    if (currentGroup?.date === date) {
      currentGroup.messages.push(message);
    } else {
      groups.push({ date, messages: [message] });
    }
    return groups;
  }, []);

export const getLastMessage = (messages: ConsumerMessage[]): ConsumerMessage | undefined =>
  messages[messages.length - 1];
